window.theme = window.theme || {};
window.slate = window.slate || {};

// Replaces Modernizr's one still-relevant job (it also fed csstransforms/
// csstransforms3d/svg feature-detection classes to a couple of CSS
// fallback rules -- .no-svg .icon and .no-csstransforms .drawer* -- for
// browsers lacking those features. Every real browser has supported all of
// them for well over a decade, so those rules are already permanently
// unreachable today; not replicating the negative classes changes nothing
// observable, just skips the detection work that always produced the same
// answer.
document.documentElement.classList.replace('no-js', 'js');

// Replaces vendor.js's queryParams.js snippet: parses location.search into
// window.Shopify.queryParams once, used by theme.Filters (theme-collection.js)
// to preserve sort_by etc. across tag-filter navigations.
window.Shopify = window.Shopify || {};
Shopify.queryParams = Object.fromEntries(new URLSearchParams(location.search));

/**
 * Vanilla port of vendor.js's jQuery prepareTransition plugin (Jonathan
 * Snook, MIT). Forces a reflow before a CSS-transitioned class change is
 * applied elsewhere, and cleans up an 'is-transitioning' class once the
 * transition finishes. The original's `disableExisting` call-site option
 * was never actually implemented by the plugin (its jQuery fn took no
 * arguments) -- dropped here too, no behavior change.
 */
theme.prepareTransition = function(el) {
  if (!el) return;

  el.addEventListener(
    'transitionend',
    function handler() {
      el.classList.remove('is-transitioning');
      el.removeEventListener('transitionend', handler);
    },
    { once: true }
  );

  var duration = parseFloat(getComputedStyle(el).transitionDuration) || 0;
  if (duration !== 0) {
    el.classList.add('is-transitioning');
    void el.offsetWidth; // force reflow so the class change above takes effect immediately
  }
};

theme.Sections = function Sections() {
  this.constructors = {};
  this.instances = [];

  document.addEventListener('shopify:section:load', this._onSectionLoad.bind(this));
  document.addEventListener('shopify:section:unload', this._onSectionUnload.bind(this));
  document.addEventListener('shopify:section:select', this._onSelect.bind(this));
  document.addEventListener('shopify:section:deselect', this._onDeselect.bind(this));
  document.addEventListener('shopify:block:select', this._onBlockSelect.bind(this));
  document.addEventListener('shopify:block:deselect', this._onBlockDeselect.bind(this));
};

theme.Sections.prototype = Object.assign({}, theme.Sections.prototype, {
  _createInstance: function(container, constructor) {
    var id = container.getAttribute('data-section-id');
    var type = container.getAttribute('data-section-type');

    constructor = constructor || this.constructors[type];

    if (constructor === undefined) {
      return;
    }

    var instance = Object.assign(new constructor(container), {
      id: id,
      type: type,
      container: container
    });

    this.instances.push(instance);
  },

  _onSectionLoad: function(evt) {
    var container = evt.target.querySelector('[data-section-id]');
    if (container) {
      this._createInstance(container);
    }
  },

  _onSectionUnload: function(evt) {
    this.instances = this.instances.filter(function(instance) {
      var isEventInstance = instance.id === evt.detail.sectionId;

      if (isEventInstance) {
        if (typeof instance.onUnload === 'function') {
          instance.onUnload(evt);
        }
      }

      return !isEventInstance;
    });
  },

  _onSelect: function(evt) {
    // eslint-disable-next-line no-shadow
    var instance = this.instances.find(function(instance) {
      return instance.id === evt.detail.sectionId;
    });

    if (instance !== undefined && typeof instance.onSelect === 'function') {
      instance.onSelect(evt);
    }
  },

  _onDeselect: function(evt) {
    // eslint-disable-next-line no-shadow
    var instance = this.instances.find(function(instance) {
      return instance.id === evt.detail.sectionId;
    });

    if (instance !== undefined && typeof instance.onDeselect === 'function') {
      instance.onDeselect(evt);
    }
  },

  _onBlockSelect: function(evt) {
    // eslint-disable-next-line no-shadow
    var instance = this.instances.find(function(instance) {
      return instance.id === evt.detail.sectionId;
    });

    if (instance !== undefined && typeof instance.onBlockSelect === 'function') {
      instance.onBlockSelect(evt);
    }
  },

  _onBlockDeselect: function(evt) {
    // eslint-disable-next-line no-shadow
    var instance = this.instances.find(function(instance) {
      return instance.id === evt.detail.sectionId;
    });

    if (instance !== undefined && typeof instance.onBlockDeselect === 'function') {
      instance.onBlockDeselect(evt);
    }
  },

  register: function(type, constructor) {
    this.constructors[type] = constructor;

    var self = this;
    document.querySelectorAll('[data-section-type="' + type + '"]').forEach(function(container) {
      self._createInstance(container, constructor);
    });
  }
});

window.slate = window.slate || {};

/**
 * iFrames
 * -----------------------------------------------------------------------------
 * Wrap videos in div to force responsive layout.
 *
 * @namespace iframes
 */

slate.rte = (function() {
  function wrapElement(el, wrapperClass) {
    var wrapper = document.createElement('div');
    wrapper.className = wrapperClass;
    el.parentNode.insertBefore(wrapper, el);
    wrapper.appendChild(el);
  }

  return {
    wrapTable: function() {
      document.querySelectorAll('.rte table').forEach(function(table) {
        wrapElement(table, 'rte__table-wrapper');
      });
    },

    iframeReset: function() {
      var iframeVideoSelector =
        '.rte iframe[src*="youtube-nocookie.com/embed"], .rte iframe[src*="player.vimeo"]';
      var iframeVideos = document.querySelectorAll(iframeVideoSelector);

      iframeVideos.forEach(function(iframe) {
        // Add wrapper to make video responsive
        wrapElement(iframe, 'video-wrapper');
      });

      var iframeResetSelector = iframeVideoSelector + ', .rte iframe#admin_bar_iframe';
      document.querySelectorAll(iframeResetSelector).forEach(function(iframe) {
        // Re-set the src attribute on each iframe after page load
        // for Chrome's "incorrect iFrame content on 'back'" bug.
        // https://code.google.com/p/chromium/issues/detail?id=395791
        // Need to specifically target video and admin bar
        iframe.src = iframe.src;
      });
    }
  };
})();

window.slate = window.slate || {};

/**
 * A11y Helpers
 * -----------------------------------------------------------------------------
 * A collection of useful functions that help make your theme more accessible
 * to users with visual impairments.
 *
 *
 * @namespace a11y
 */

slate.a11y = (function() {
  var trapFocusHandlers = {};

  return {
    /**
     * For use when focus shifts to a container rather than a link
     * eg for In-page links, after scroll, focus shifts to content area so that
     * next `tab` is where user expects
     *
     * @param {Element} element - The element to be acted upon
     */
    pageLinkFocus: function(element) {
      if (!element) return;

      var focusClass = 'js-focus-hidden';

      function callback() {
        element.classList.remove(focusClass);
        element.removeAttribute('tabindex');
      }

      element.setAttribute('tabIndex', '-1');
      element.focus();
      element.classList.add(focusClass);
      element.addEventListener('blur', callback, { once: true });
    },

    /**
     * Traps the focus in a particular container
     *
     * @param {object} options - Options to be used
     * @param {Element} options.container - Container to trap focus within
     * @param {Element} options.elementToFocus - Element to be focused when focus leaves container
     * @param {string} options.namespace - Namespace used for new focus event handler
     */
    trapFocus: function(options) {
      var namespace = options.namespace || 'default';

      if (!options.elementToFocus) {
        options.elementToFocus = options.container;
      }

      options.container.setAttribute('tabindex', '-1');
      options.elementToFocus.focus();

      var handler = function(evt) {
        if (options.container !== evt.target && !options.container.contains(evt.target)) {
          options.container.focus();
        }
      };

      trapFocusHandlers[namespace] = handler;
      document.addEventListener('focusin', handler);
    },

    /**
     * Removes the trap of focus in a particular container
     *
     * @param {object} options - Options to be used
     * @param {Element} options.container - Container to trap focus within
     * @param {string} options.namespace - Namespace used for new focus event handler
     */
    removeTrapFocus: function(options) {
      var namespace = options.namespace || 'default';

      if (options.container) {
        options.container.removeAttribute('tabindex');
      }

      var handler = trapFocusHandlers[namespace];
      if (handler) {
        document.removeEventListener('focusin', handler);
        delete trapFocusHandlers[namespace];
      }
    }
  };
})();

window.Drawers = (function() {
  var Drawer = function(id, position, options) {
    var defaults = {
      close: '.js-drawer-close',
      open: '.js-drawer-open-' + position,
      openClass: 'js-drawer-open',
      dirOpenClass: 'js-drawer-open-' + position
    };

    this.nodes = {
      parent: [document.body, document.documentElement],
      page: document.querySelectorAll('.page-element'),
      moved: document.querySelectorAll('.is-moved-by-drawer')
    };

    this.config = Object.assign(defaults, options);
    this.position = position;

    this.drawer = document.getElementById(id);
    this.openEls = document.querySelectorAll(this.config.open);

    if (!this.drawer) {
      return;
    }

    this.drawerIsOpen = false;
    this.init();
  };

  Drawer.prototype.init = function() {
    var self = this;

    this.openEls.forEach(function(el) {
      el.setAttribute('aria-expanded', 'false');
      el.addEventListener('click', self.open.bind(self));
    });

    this.drawer.querySelectorAll(this.config.close).forEach(function(el) {
      el.addEventListener('click', self.close.bind(self));
    });
  };

  Drawer.prototype.open = function(evt) {
    // Keep track if drawer was opened from a click, or called by another function
    var externalCall = false;

    // don't open an opened drawer
    if (this.drawerIsOpen) {
      return;
    }

    this.openEls.forEach(function(el) {
      el.classList.add(this.config.openClass);
    }, this);

    // Prevent following href if link is clicked
    if (evt) {
      evt.preventDefault();
    } else {
      externalCall = true;
    }

    // Without this, the drawer opens, the click event bubbles up to nodes.page
    // which closes the drawer.
    if (evt && evt.stopPropagation) {
      evt.stopPropagation();
      // save the source of the click, we'll focus to this on close
      this.activeSource = evt.currentTarget;
    }

    if (this.drawerIsOpen && !externalCall) {
      return this.close();
    }

    // Add is-transitioning class to moved elements on open so drawer can have
    // transition for close animation
    this.nodes.moved.forEach(function(el) {
      el.classList.add('is-transitioning');
    });
    theme.prepareTransition(this.drawer);

    this.nodes.parent.forEach(function(el) {
      el.classList.add(this.config.openClass, this.config.dirOpenClass);
    }, this);
    this.drawerIsOpen = true;

    // Set focus on drawer
    slate.a11y.trapFocus({
      container: this.drawer,
      namespace: 'drawer_focus'
    });

    // Run function when draw opens if set
    if (
      this.config.onDrawerOpen &&
      typeof this.config.onDrawerOpen === 'function'
    ) {
      if (!externalCall) {
        this.config.onDrawerOpen();
      }
    }

    if (this.activeSource && this.activeSource.getAttribute('aria-expanded') !== null) {
      this.activeSource.setAttribute('aria-expanded', 'true');
    }

    this.bindEvents();
  };

  Drawer.prototype.close = function() {
    // don't close a closed drawer
    if (!this.drawerIsOpen) {
      return;
    }

    this.openEls.forEach(function(el) {
      el.classList.remove(this.config.openClass);
    }, this);

    // deselect any focused form elements
    if (document.activeElement) document.activeElement.blur();

    // Ensure closing transition is applied to moved elements, like the nav
    this.nodes.moved.forEach(function(el) {
      theme.prepareTransition(el);
    });
    theme.prepareTransition(this.drawer);

    this.nodes.parent.forEach(function(el) {
      el.classList.remove(this.config.dirOpenClass, this.config.openClass);
    }, this);

    this.drawerIsOpen = false;

    // Remove focus on drawer
    slate.a11y.removeTrapFocus({
      container: this.drawer,
      namespace: 'drawer_focus'
    });

    if (this.activeSource && this.activeSource.getAttribute('aria-expanded') !== null) {
      this.activeSource.setAttribute('aria-expanded', 'false');
    }

    this.unbindEvents();
  };

  Drawer.prototype.bindEvents = function() {
    var self = this;

    this._onPageTouchMoveHandler = function(evt) {
      evt.preventDefault();
    };
    this._onPageClickHandler = function(evt) {
      evt.preventDefault();
      evt.stopPropagation();
      self.close();
    };
    this._onParentKeyupHandler = function(evt) {
      if (evt.keyCode === 27) {
        self.close();
      }
    };

    // Lock scrolling on mobile
    this.nodes.page.forEach(function(el) {
      el.addEventListener('touchmove', self._onPageTouchMoveHandler, { passive: false });
      // Clicking out of drawer closes it
      el.addEventListener('click', self._onPageClickHandler);
    });

    // Pressing escape closes drawer
    this.nodes.parent.forEach(function(el) {
      el.addEventListener('keyup', self._onParentKeyupHandler);
    });
  };

  Drawer.prototype.unbindEvents = function() {
    var self = this;

    this.nodes.page.forEach(function(el) {
      el.removeEventListener('touchmove', self._onPageTouchMoveHandler);
      el.removeEventListener('click', self._onPageClickHandler);
    });

    this.nodes.parent.forEach(function(el) {
      el.removeEventListener('keyup', self._onParentKeyupHandler);
    });
  };

  return Drawer;
})();

window.Meganav = (function() {
  function siblings(el, selector) {
    var result = [];
    var parent = el.parentElement;
    if (!parent) return result;
    Array.prototype.forEach.call(parent.children, function(child) {
      if (child !== el && child.matches(selector)) {
        result.push(child);
      }
    });
    return result;
  }

  function directChild(parentEl, selector) {
    if (!parentEl) return null;
    return (
      Array.prototype.filter.call(parentEl.children, function(child) {
        return child.matches(selector);
      })[0] || null
    );
  }

  function forEachEl(list, fn) {
    Array.prototype.forEach.call(list || [], fn);
  }

  var Meganav = function(options) {
    this.page = document.querySelectorAll('.page-element');

    var defaults = {
      meganavsSelector: '.meganav',
      meganavToggleSelector: '.meganav-toggle',
      meganavDropdownContainers: document.querySelectorAll('.site-nav__dropdown-container'),
      meganavToggleThirdLevel: document.querySelectorAll('.meganav__link-toggle'),
      meganavLinkSecondLevel: document.querySelectorAll('.meganav__link--second-level'),
      meganavLinkThirdLevel: document.querySelectorAll('.meganav__link--third-level'),
      meganavDropdownThirdLevelSelector: '.site-nav__dropdown--third-level',
      isOpen: false,
      preventDuplicates: false,
      closeOnPageClick: false,
      closeThirdLevelOnBlur: false,
      activeClass: 'meganav--active',
      drawerClass: 'meganav--drawer',
      meganavDropdown: '.site-nav__dropdown',
      drawerToggleClass: 'drawer__nav-toggle-btn',
      drawerNavItem: '.drawer__nav-item',
      thirdLevelClass: 'meganav__link-toggle',
      noAnimationClass: 'meganav--no-animation'
    };

    this.config = Object.assign(defaults, options);
    this.toggleEls = document.querySelectorAll(this.config.meganavToggleSelector);
    this.init();
  };

  Meganav.prototype.init = function() {
    var self = this;

    forEachEl(this.toggleEls, function(el) {
      el.addEventListener('click', self.requestMeganav.bind(self));
    });

    if (this.config.closeThirdLevelOnBlur) {
      forEachEl(this.config.meganavLinkThirdLevel, function(el) {
        el.addEventListener('blur', self.closeThirdLevelMenu.bind(self));
      });
    }
  };

  Meganav.prototype.requestMeganav = function(evt) {
    var targetedMeganav;

    // Prevent following href if link is clicked
    if (evt) {
      evt.preventDefault();
    }

    // Without this, the meganav opens, the click event bubbles up to
    // this.page which closes the drawer.
    if (evt && evt.stopPropagation) {
      evt.stopPropagation();
    }

    var el = evt.currentTarget;
    var anotherNavIsOpen = this.config.isOpen;
    var isThirdLevelBtn = el.classList.contains(this.config.thirdLevelClass);

    // The targeted meganav is different for the drawer and non-drawer navs
    if (el.classList.contains(this.config.drawerToggleClass)) {
      var navItem = el.closest(this.config.drawerNavItem);
      targetedMeganav = directChild(navItem, '.' + this.config.drawerClass);
    } else {
      targetedMeganav = siblings(el, this.config.meganavDropdown)[0] || null;
    }

    // Navigate to link href or close menu if already active
    if (el.classList.contains(this.config.activeClass) && el.tagName === 'A') {
      window.location = el.getAttribute('href');
      return;
    }

    // If true, don't let multiple meganavs be open simultaneously
    if (!isThirdLevelBtn && this.config.preventDuplicates) {
      this.close();
    }

    if (!targetedMeganav) return;

    if (targetedMeganav.classList.contains(this.config.drawerClass)) {
      var isExpanded = el.getAttribute('aria-expanded') === 'true';

      el.classList.toggle(this.config.activeClass);
      el.setAttribute('aria-expanded', String(!isExpanded));

      this._slideToggle(targetedMeganav, 200);
    } else {
      el.classList.add(this.config.activeClass);
      el.setAttribute('aria-expanded', 'true');

      // Show targeted nav, letting it know whether another meganav is already open
      this.open(el, targetedMeganav, anotherNavIsOpen);
    }

    // Setup event handlers when meganav is open
    this.bindEvents();
    this.config.isOpen = true;
  };

  // Vanilla replacement for jQuery's .slideToggle(duration). Needed
  // specifically here because .drawer__nav .meganav is display:none by
  // default in CSS, so there's nothing for a pure opacity/visibility
  // transition to animate from -- unlike the non-drawer dropdown, which is
  // handled entirely by CSS (see .meganav.meganav--active in theme.css) and
  // needs no JS animation at all, just the class toggle above.
  Meganav.prototype._slideToggle = function(el, duration) {
    var isHidden = getComputedStyle(el).display === 'none';

    el.style.transition = 'none';

    if (isHidden) {
      el.style.display = 'block';
      var targetHeight = el.scrollHeight;
      el.style.height = '0px';
      void el.offsetHeight; // force reflow so the height:0 above takes effect first
      el.style.transition = 'height ' + duration + 'ms ease';
      el.style.height = targetHeight + 'px';

      el.addEventListener(
        'transitionend',
        function handler() {
          el.style.height = '';
          el.style.transition = '';
          el.removeEventListener('transitionend', handler);
        },
        { once: true }
      );
    } else {
      var currentHeight = el.scrollHeight;
      el.style.height = currentHeight + 'px';
      void el.offsetHeight; // force reflow
      el.style.transition = 'height ' + duration + 'ms ease';
      el.style.height = '0px';

      el.addEventListener(
        'transitionend',
        function handler() {
          el.style.display = 'none';
          el.style.height = '';
          el.style.transition = '';
          el.removeEventListener('transitionend', handler);
        },
        { once: true }
      );
    }
  };

  Meganav.prototype.open = function(el, target, noAnimation) {
    var isThirdLevelBtn = el.classList.contains(this.config.thirdLevelClass);

    target.classList.add(this.config.activeClass);

    if (isThirdLevelBtn) {
      this.toggleSubNav(el, target);
    }

    // Add class to override animation - CSS determined
    if (noAnimation) {
      target.classList.add(this.config.noAnimationClass);
    }
  };

  Meganav.prototype.toggleSubNav = function(el) {
    this.removeMenuActiveState();

    el.classList.add(this.config.activeClass);
    el.setAttribute('aria-expanded', 'true');

    siblings(el, this.config.meganavDropdownThirdLevelSelector).forEach(function(sibling) {
      sibling.classList.add(this.config.activeClass);
    }, this);

    if (el.parentElement) {
      el.parentElement.classList.add(this.config.activeClass);
    }
  };

  Meganav.prototype.close = function(evt, target) {
    var self = this;

    if (this.config.preventDuplicates) {
      // Close all meganavs
      document.querySelectorAll(this.config.meganavsSelector).forEach(function(el) {
        el.classList.remove(self.config.activeClass, self.config.noAnimationClass);
      });
      forEachEl(this.toggleEls, function(el) {
        el.classList.remove(self.config.activeClass);
        el.setAttribute('aria-expanded', 'false');
      });
      forEachEl(this.config.meganavDropdownContainers, function(el) {
        el.classList.remove(self.config.activeClass);
      });
    } else if (target) {
      // Close targeted nav
      var targetId = target.getAttribute('aria-controls');
      var targetedMeganav = targetId ? document.getElementById(targetId) : null;
      if (targetedMeganav) {
        targetedMeganav.classList.remove(this.config.activeClass, this.config.noAnimationClass);
      }
      target.classList.remove(this.config.activeClass);
      target.setAttribute('aria-expanded', 'false');
    }

    // Remove event listeners
    this.unbindEvents();
    this.config.isOpen = false;
  };

  Meganav.prototype.closeThirdLevelMenu = function(evt) {
    var el = evt.currentTarget;
    var parent = el.parentElement;

    if (!parent || !parent.parentElement || parent !== parent.parentElement.lastElementChild) {
      return;
    }

    var self = this;
    forEachEl(this.config.meganavLinkSecondLevel, function(secondLevelEl) {
      secondLevelEl.addEventListener(
        'focus',
        function handler() {
          self.removeMenuActiveState();
          secondLevelEl.removeEventListener('focus', handler);
        },
        { once: true }
      );
    });
  };

  Meganav.prototype.removeMenuActiveState = function() {
    var self = this;

    forEachEl(this.config.meganavToggleThirdLevel, function(el) {
      el.classList.remove(self.config.activeClass, self.config.noAnimationClass);
      el.setAttribute('aria-expanded', 'false');
    });

    document.querySelectorAll(this.config.meganavDropdownThirdLevelSelector).forEach(function(el) {
      el.classList.remove(self.config.activeClass, self.config.noAnimationClass);
    });

    forEachEl(this.config.meganavDropdownContainers, function(el) {
      el.classList.remove(self.config.activeClass);
    });
  };

  Meganav.prototype.bindEvents = function() {
    if (!this.config.closeOnPageClick) {
      return;
    }

    var self = this;

    // Clicking away from the meganav will close it
    this._onPageClickHandler = function(evt) {
      self.close(evt);
    };
    forEachEl(this.page, function(el) {
      el.addEventListener('click', self._onPageClickHandler);
    });

    // Exception to above: clicking anywhere on the meganav will NOT close it
    this._onMeganavClickHandler = function(evt) {
      evt.stopPropagation();
    };
    document.querySelectorAll(this.config.meganavsSelector).forEach(function(el) {
      el.addEventListener('click', self._onMeganavClickHandler);
    });

    // Pressing escape closes meganav and focuses the target parent link
    this._onDocumentKeyupHandler = function(evt) {
      if (evt.keyCode !== 27) return;

      var activeToggle = null;
      forEachEl(self.toggleEls, function(el) {
        if (el.classList.contains(self.config.activeClass)) activeToggle = el;
      });
      if (activeToggle) activeToggle.focus();
      self.close();
    };
    document.addEventListener('keyup', this._onDocumentKeyupHandler);
  };

  Meganav.prototype.unbindEvents = function() {
    if (!this.config.closeOnPageClick) {
      return;
    }

    var self = this;

    if (this._onPageClickHandler) {
      forEachEl(this.page, function(el) {
        el.removeEventListener('click', self._onPageClickHandler);
      });
    }
    if (this._onMeganavClickHandler) {
      document.querySelectorAll(this.config.meganavsSelector).forEach(function(el) {
        el.removeEventListener('click', self._onMeganavClickHandler);
      });
    }
    if (this._onDocumentKeyupHandler) {
      document.removeEventListener('keyup', this._onDocumentKeyupHandler);
    }
  };

  return Meganav;
})();

window.Notify = (function() {
  var notify = function() {
    this.initCache();

    this.settings = {
      notifyActiveClass: 'notification--active',
      closeTimer: 5000,
      promoKeyName: 'promo-' + (this.cache.notificationPromo ? this.cache.notificationPromo.dataset.text : undefined)
    };

    this.notifyTimer = null;
    this.lastFocusedElement = null;

    this.isLocalStorageSupported = isLocalStorageSupported();

    var self = this;
    this.cache.notificationClose.forEach(function(el) {
      el.addEventListener('click', self.close.bind(self));
    });
    this.showPromo();
  };

  function isLocalStorageSupported() {
    // Return false if we are in an iframe without access to sessionStorage
    if (window.self !== window.top) {
      return false;
    }

    var testKey = 'test';
    try {
      var storage = window.sessionStorage;
      storage.setItem(testKey, '1');
      storage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  }

  notify.prototype.initCache = function() {
    this.cache = {
      notificationSuccess: document.getElementById('NotificationSuccess'),
      notificationSuccessLink: document.querySelector('#NotificationSuccess a'),
      notificationError: document.getElementById('NotificationError'),
      notificationPromo: document.getElementById('NotificationPromo'),
      notificationClose: document.querySelectorAll('.notification__close'),
      notificationErrorMessage: document.querySelector('.notification__message--error')
    };
  };

  notify.prototype.open = function(state, message, autoclose) {
    var self = this;
    this.close();

    if (state === 'success') {
      if (this.cache.notificationSuccess) {
        this.cache.notificationSuccess.setAttribute('aria-hidden', 'false');
        this.cache.notificationSuccess.classList.add(this.settings.notifyActiveClass);

        // Set last focused element to return to once success
        // notification is dismissed (by timeout)
        this.lastFocusedElement = document.activeElement;

        // Set focus on link to cart after transition
        this.cache.notificationSuccess.addEventListener(
          'transitionend',
          function() {
            slate.a11y.pageLinkFocus(self.cache.notificationSuccessLink);
          },
          { once: true }
        );

        // Fallback for no transitions
        if (
          document.documentElement.classList.contains('no-csstransitions') ||
          document.body.classList.contains('no-csstransitions')
        ) {
          slate.a11y.pageLinkFocus(this.cache.notificationSuccessLink);
        }
      }
    } else {
      if (this.cache.notificationErrorMessage) {
        this.cache.notificationErrorMessage.innerHTML = message;
      }
      if (this.cache.notificationError) {
        this.cache.notificationError.setAttribute('aria-hidden', 'false');
        this.cache.notificationError.classList.add(this.settings.notifyActiveClass);
      }
    }

    // Timeout to close the notification
    if (autoclose) {
      clearTimeout(this.notifyTimer);
      this.notifyTimer = setTimeout(
        this.close.bind(this),
        this.settings.closeTimer
      );
    }
  };

  notify.prototype.close = function(evt) {
    if (evt && evt.currentTarget && evt.currentTarget.id === 'NotificationPromoClose') {
      if (this.isLocalStorageSupported) {
        localStorage.setItem(this.settings.promoKeyName, 'hidden');
      }
    }

    // Return focus to previous element if one is defined
    // and the user has not strayed from the success notification link
    if (
      this.lastFocusedElement &&
      this.cache.notificationSuccessLink &&
      document.activeElement === this.cache.notificationSuccessLink
    ) {
      slate.a11y.pageLinkFocus(this.lastFocusedElement);
    }

    // Close all notification bars
    if (this.cache.notificationSuccess) {
      this.cache.notificationSuccess.setAttribute('aria-hidden', 'true');
      this.cache.notificationSuccess.classList.remove(this.settings.notifyActiveClass);
    }
    if (this.cache.notificationError) {
      this.cache.notificationError.setAttribute('aria-hidden', 'true');
      this.cache.notificationError.classList.remove(this.settings.notifyActiveClass);
    }
    if (this.cache.notificationPromo) {
      this.cache.notificationPromo.setAttribute('aria-hidden', 'true');
      this.cache.notificationPromo.classList.remove(this.settings.notifyActiveClass);
    }

    // Reset last focused element
    this.lastFocusedElement = null;
  };

  notify.prototype.showPromo = function(SFEevent) {
    // If reloaded in the storefront editor, update selectors/settings
    if (SFEevent) {
      this.initCache();
    }

    if (
      this.isLocalStorageSupported &&
      localStorage[this.settings.promoKeyName] === 'hidden'
    ) {
      return;
    }

    if (this.cache.notificationPromo) {
      this.cache.notificationPromo.setAttribute('aria-hidden', 'false');
      this.cache.notificationPromo.classList.add(this.settings.notifyActiveClass);
    }
  };

  return notify;
})();

theme.stickyHeader = (function() {
  var selectors = {
    searchCartWrapper: '#SiteNavSearchCart',
    stickyNavSearchCart: '#StickyNavSearchCart'
  };

  function init() {
    var siteNavSearchCart = document.querySelector(selectors.searchCartWrapper);
    var stickyNavSearchCart = document.querySelector(selectors.stickyNavSearchCart);

    // Clone search/cart icons into fixed nav
    if (siteNavSearchCart && stickyNavSearchCart && siteNavSearchCart.childNodes.length) {
      siteNavSearchCart.childNodes.forEach(function(node) {
        stickyNavSearchCart.appendChild(node.cloneNode(true));
      });
    }
  }

  function unload() {
    // Nothing to unbind -- init() no longer attaches a scroll listener
    // (the previous scroll-to-stick behavior was already commented out and
    // unreachable before this file was converted from jQuery).
  }

  return {
    init: init,
    unload: unload
  };
})();

theme.headerNav = (function() {
  var selectors = {
    siteNav: '#SiteNav',
    siteNavCompressed: '#SiteNavCompressed',
    siteNavParent: '#SiteNavParent',
    siteNavItem: '.site-nav__item',
    stickyNavWrapper: '#StickNavWrapper',
    stickyNav: '#StickyNav'
  };

  var config = {
    searchShowClass: 'site-header__search-input--visible',
    searchInputClass: 'header-search-input',
    searchSubmitClass: 'header-search-submit',
    searchInnerActiveClass: 'search-header__search-inner--active'
  };

  var onResizeHandler;

  function debounce(delay, fn) {
    var timer;
    return function() {
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function() {
        fn.apply(null, args);
      }, delay);
    };
  }

  function init() {
    sizeNav();
    initMegaNavs();
    initHeaderSearch();

    onResizeHandler = debounce(250, sizeNav);
    window.addEventListener('resize', onResizeHandler);
  }

  function sizeNav() {
    var navWidth = 0;
    var navParentEl = document.querySelector(selectors.siteNavParent);
    var parentWidth = navParentEl ? navParentEl.getBoundingClientRect().width : 0;
    var hideClass = 'hide';
    var stickyNavWrapperEl = document.querySelector(selectors.stickyNavWrapper);
    var stickyNavEl = document.querySelector(selectors.stickyNav);
    var siteNavEl = document.querySelector(selectors.siteNav);
    var siteNavCompressedEl = document.querySelector(selectors.siteNavCompressed);

    // Set height on nav-bar wrapper so when fixed, doesn't break layout
    if (stickyNavWrapperEl && stickyNavEl) {
      stickyNavWrapperEl.style.height = stickyNavEl.getBoundingClientRect().height + 'px';
    }

    // Calculate the width of each nav item
    // after forcing them to be visible for the calculations
    if (siteNavEl) siteNavEl.classList.remove(hideClass);
    document.querySelectorAll(selectors.siteNavItem).forEach(function(el) {
      navWidth += el.getBoundingClientRect().width;
    });

    if (navWidth > parentWidth) {
      // Intentionally a no-op -- the compressed-nav swap here was already
      // commented out before this file was converted from jQuery.
    } else {
      if (siteNavEl) siteNavEl.classList.remove(hideClass);
      if (siteNavCompressedEl) siteNavCompressedEl.classList.add(hideClass);
    }
  }

  function initMegaNavs() {
    // Each construction wrapped individually so a throw in one (eg. missing
    // elements for one nav variant) can't skip the other two.
    try {
      new window.Meganav({
        meganavsSelector: '.site-nav__dropdown',
        meganavToggleSelector: '.site-nav__link-toggle',
        preventDuplicates: true,
        closeOnPageClick: true,
        closeThirdLevelOnBlur: true
      });
    } catch (err) {
      console.error('[theme.headerNav] site-nav Meganav threw:', err);
    }

    try {
      new window.Meganav({
        meganavsSelector: '.meganav--index',
        meganavToggleSelector: '.index__meganav-toggle'
      });
    } catch (err) {
      console.error('[theme.headerNav] index Meganav threw:', err);
    }

    try {
      new window.Meganav({
        meganavsSelector: '.meganav--drawer',
        meganavToggleSelector: '.drawer__meganav-toggle'
      });
    } catch (err) {
      console.error('[theme.headerNav] drawer Meganav threw:', err);
    }
  }

  function initHeaderSearch() {
    // This function runs after the header search element
    // is duplicated into the sticky nav, meaning there are
    // two search fields to be aware of at this point.
    document.querySelectorAll('.header-search').forEach(function(form) {
      var input = form.querySelector('.' + config.searchInputClass);
      var submit = form.querySelector('.' + config.searchSubmitClass);
      var searchInner = document.querySelector('.header-search-inner');

      if (!input) return;

      input.addEventListener('focus', function() {
        if (searchInner) searchInner.classList.add(config.searchInnerActiveClass);
        input.classList.add(config.searchShowClass);
      });

      input.addEventListener('blur', function() {
        if (searchInner) searchInner.classList.remove(config.searchInnerActiveClass);
        if (input.value) {
          input.classList.add(config.searchShowClass);
        } else {
          input.classList.remove(config.searchShowClass);
        }
      });

      if (submit) {
        submit.addEventListener('click', function(evt) {
          if (input.classList.contains(config.searchShowClass)) {
            return;
          }

          evt.preventDefault();
          input.classList.add(config.searchShowClass);
          input.focus();
        });
      }
    });
  }

  function unload() {
    if (onResizeHandler) {
      window.removeEventListener('resize', onResizeHandler);
    }
  }

  return {
    init: init,
    unload: unload
  };
})();

theme.HeaderSection = (function() {
  // Each step wrapped individually: this constructor runs first, before any
  // other section is registered (see theme.init below), and an uncaught
  // throw here would otherwise propagate out of theme.Sections.register()'s
  // forEach and silently skip registering every section after it, on every
  // page (since every page has a header) -- a bigger-blast-radius version of
  // the $(theme.init)/typeof-guard race fixed earlier in this file.
  function Header() {
    try {
      theme.stickyHeader.init();
    } catch (err) {
      console.error('[theme.HeaderSection] stickyHeader.init() threw:', err);
    }
    try {
      theme.headerNav.init();
    } catch (err) {
      console.error('[theme.HeaderSection] headerNav.init() threw:', err);
    }
    try {
      theme.Notify = new window.Notify();
    } catch (err) {
      console.error('[theme.HeaderSection] new window.Notify() threw:', err);
    }
    try {
      theme.NavDrawer = new window.Drawers('NavDrawer', 'left');
    } catch (err) {
      console.error('[theme.HeaderSection] new window.Drawers() threw:', err);
    }
    try {
      drawerSearch();
    } catch (err) {
      console.error('[theme.HeaderSection] drawerSearch() threw:', err);
    }
  }

  function drawerSearch() {
    var drawerSearchEl = document.querySelector('.drawer__search-input');
    var drawerSearchSubmitEl = document.querySelector('.drawer__search-submit');

    if (!drawerSearchEl || !drawerSearchSubmitEl) return;

    drawerSearchSubmitEl.addEventListener('click', function(evt) {
      if (drawerSearchEl.value.length !== 0) {
        return;
      }

      evt.preventDefault();
      drawerSearchEl.focus();
    });
  }

  Header.prototype = Object.assign({}, Header.prototype, {
    onUnload: function() {
      theme.stickyHeader.unload();
      theme.headerNav.unload();
    }
  });

  return Header;
})();

theme.init = function() {
  slate.rte.wrapTable();
  slate.rte.iframeReset();

  // Common a11y fixes
  var hashEl = window.location.hash ? document.querySelector(window.location.hash) : null;
  slate.a11y.pageLinkFocus(hashEl);

  document.querySelectorAll('.in-page-link').forEach(function(el) {
    el.addEventListener('click', function(evt) {
      var targetEl = evt.currentTarget.hash ? document.querySelector(evt.currentTarget.hash) : null;
      slate.a11y.pageLinkFocus(targetEl);
    });
  });

  document.querySelectorAll('a[href="#"]').forEach(function(el) {
    el.addEventListener('click', function(evt) {
      evt.preventDefault();
    });
  });

  // Sections
  // Page-specific modules (Product, Cart, Filters, CollectionsList) only
  // exist when their page-specific bundle is also loaded -- guard each
  // registration so this still works regardless of which pages load which
  // scripts. theme.Sections.register() is a no-op for a type with no
  // matching elements, and _createInstance() already no-ops on an
  // undefined constructor, so these checks just avoid registering a type
  // whose module was never loaded in the first place.
  var sections = new theme.Sections();
  sections.register('header', theme.HeaderSection);
  if (typeof theme.Product !== 'undefined') {
    sections.register('product', theme.Product);
  }
  if (typeof theme.Filters !== 'undefined') {
    sections.register('collection-filters', theme.Filters);
  }
  if (typeof theme.Cart !== 'undefined') {
    sections.register('cart', theme.Cart);
  }
  if (typeof theme.CollectionsList !== 'undefined') {
    sections.register('collections-list', theme.CollectionsList);
  }
};

// Registration checks each page-specific module with typeof theme.X above,
// since those only exist once their own deferred <script> has executed.
// $(document).ready() resolves through jQuery's own internal ready-queue,
// which can fire via a setTimeout that occasionally beats a still-pending
// deferred script to the punch on a cold/uncached load (much more likely
// than on a warm refresh, where every deferred script executes in a tight
// back-to-back sequence) -- when that happens, theme.init() runs before
// e.g. theme.Product is defined, and that section's typeof check silently
// (and permanently, for that page view) skips registering it. The native
// DOMContentLoaded event is spec-guaranteed to fire only after every
// deferred script has finished executing, with no such race possible.
document.addEventListener('DOMContentLoaded', theme.init);

document.addEventListener("DOMContentLoaded", function() {
    const audioPlayers = document.querySelectorAll("audio");

    audioPlayers.forEach(audio => {
        audio.addEventListener("play", function() {
            audioPlayers.forEach(otherAudio => {
                if (otherAudio !== audio) {
                    otherAudio.pause();
                }
            });
        });
    });
});
